/**
 * Hikvision NVR / DVR deep-dive catalog — second-pass research expanding the
 * 12-SKU first pass to broad coverage across Value, Pro, Ultra and Turbo HD
 * AcuSense families. All entries below were verified against primary Hikvision
 * datasheets or reseller-hosted datasheet PDFs / spec pages; each entry cites
 * its source URL inline. Specs that the datasheet does not state are left
 * undefined rather than guessed.
 *
 * Skipped (already present in hikvisionCatalog.ts):
 *   DS-7716NXI-K4/16P, DS-7732NI-I4/16P, DS-7732NXI-K4, DS-7608NXI-K2/8P,
 *   DS-7608NI-K2/8P, DS-7716NI-K4, DS-8608NI-K8, DS-8616NI-K8, DS-8632NI-K8,
 *   DS-9664NI-I8, iDS-7204HUHI-M1/S, DS-7216HQHI-K2.
 *
 * Form-factor note: the NVRSpec union only allows '1U'/'2U' etc. Datasheet
 * "1.5U" is mapped to '2U' (nearest larger U size, since a 1.5U unit needs a
 * 2U rack slot in practice).
 */

import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const HIKVISION_NVR_DEEP_DIVE: EquipmentCatalogEntry[] = [
  // ─────────────────────────────────────────────────────────────────────
  // Value Series 7100 (Mini 1U, 1 HDD)
  // ─────────────────────────────────────────────────────────────────────

  // https://www.sourcesecurity.com/hikvision-ds-7104ni-q1-m-network-video-recorder-nvr-technical-details.html
  // (Spec sheet: assets.hikvision.com/.../Datasheet-of-DS-7104NI-Q1_M_Value-7-Series-NVR_V4.73.200_20230404.pdf)
  {
    id: 'hikvision-ds-7104ni-q1-m',
    manufacturer: 'Hikvision',
    model: 'DS-7104NI-Q1/M',
    category: 'nvr-recorder',
    tagline: '4-ch entry NVR, 1 HDD up to 6 TB, no PoE — Mini 1U',
    verified: true,
    datasheetUrl:
      'https://www.sourcesecurity.com/hikvision-ds-7104ni-q1-m-network-video-recorder-nvr-technical-details.html',
    specs: {
      channels: 4,
      inputBandwidthMbps: 40,
      outputBandwidthMbps: 60,
      maxResolution: '4 MP',
      hddBays: 1,
      hddBayMaxTb: 6,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U', // Mini 1U
      networkPorts: '1× 10/100 Mbps RJ-45',
    },
  },

  // https://www.hikvision.com/en/products/IP-Products/Network-Video-Recorders/Value-Series/ds-7104ni-q1-4p-m/
  // (Datasheet PDF: assets.hikvision.com/.../Datasheet-of-DS-7104NI-Q1_4P_M_Value-7-Series-NVR_V4.73.200_20230404.pdf)
  {
    id: 'hikvision-ds-7104ni-q1-4p-m',
    manufacturer: 'Hikvision',
    model: 'DS-7104NI-Q1/4P/M',
    category: 'nvr-recorder',
    tagline: '4-ch Mini 1U NVR with 4-port PoE, 1 HDD up to 6 TB',
    verified: true,
    datasheetUrl:
      'https://www.hikvision.com/en/products/IP-Products/Network-Video-Recorders/Value-Series/ds-7104ni-q1-4p-m/',
    specs: {
      channels: 4,
      inputBandwidthMbps: 40,
      outputBandwidthMbps: 60,
      maxResolution: '6 MP',
      hddBays: 1,
      hddBayMaxTb: 6,
      poeInPorts: 4,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U', // Mini 1U
      networkPorts: '1× 10/100 Mbps RJ-45 + 4× PoE 10/100',
    },
  },

  // https://www.hikvision.com/en/products/IP-Products/Network-Video-Recorders/Value-Series/ds-7108ni-q1-8p-m/
  // (Datasheet PDF: assets.hikvision.com/.../Datasheet-of-DS-7108NI-Q1_8P_M_Value-7-Series-NVR_V4.73.200_20230404.pdf)
  {
    id: 'hikvision-ds-7108ni-q1-8p-m',
    manufacturer: 'Hikvision',
    model: 'DS-7108NI-Q1/8P/M',
    category: 'nvr-recorder',
    tagline: '8-ch Mini 1U NVR with 8-port PoE, 1 HDD up to 6 TB',
    verified: true,
    datasheetUrl:
      'https://www.hikvision.com/en/products/IP-Products/Network-Video-Recorders/Value-Series/ds-7108ni-q1-8p-m/',
    specs: {
      channels: 8,
      inputBandwidthMbps: 60,
      outputBandwidthMbps: 60,
      maxResolution: '6 MP',
      hddBays: 1,
      hddBayMaxTb: 6,
      poeInPorts: 8,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U', // Mini 1U
      networkPorts: '1× 10/100 Mbps RJ-45 + 8× PoE 10/100',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Pro Series 7600 K1 (1 HDD)
  // ─────────────────────────────────────────────────────────────────────

  // https://www.sourcesecurity.com/hikvision-ds-7604ni-k1-4p-network-video-recorder-nvr-technical-details.html
  // (Hikvision PDF: assets.hikvision.com/.../Datasheet-of-DS-7604NI-K1_4P-NVRD_V4.71.200_20221031.pdf)
  {
    id: 'hikvision-ds-7604ni-k1-4p',
    manufacturer: 'Hikvision',
    model: 'DS-7604NI-K1/4P',
    category: 'nvr-recorder',
    tagline: '4-ch 4K NVR with 4-port PoE, 1 HDD up to 6 TB',
    verified: true,
    datasheetUrl:
      'https://www.sourcesecurity.com/hikvision-ds-7604ni-k1-4p-network-video-recorder-nvr-technical-details.html',
    specs: {
      channels: 4,
      inputBandwidthMbps: 40,
      outputBandwidthMbps: 80,
      maxResolution: '8 MP',
      hddBays: 1,
      hddBayMaxTb: 6,
      poeInPorts: 4,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // https://www.hikvision.com/en/products/IP-Products/Network-Video-Recorders/Pro-Series/ds-7604ni-k1/
  // (Datasheet PDF: www.hikvision.com/.../Datasheet-of-DS-7604NI-K1-NVRD_V4.71.200_20221031.pdf)
  {
    id: 'hikvision-ds-7604ni-k1',
    manufacturer: 'Hikvision',
    model: 'DS-7604NI-K1',
    category: 'nvr-recorder',
    tagline: '4-ch 4K NVR, 1 HDD up to 6 TB, no built-in PoE',
    verified: true,
    datasheetUrl:
      'https://www.hikvision.com/en/products/IP-Products/Network-Video-Recorders/Pro-Series/ds-7604ni-k1/',
    specs: {
      channels: 4,
      inputBandwidthMbps: 40,
      outputBandwidthMbps: 80,
      maxResolution: '8 MP',
      hddBays: 1,
      hddBayMaxTb: 6,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // https://www.sourcesecurity.com/hikvision-ds-7608ni-k1-8p-network-video-recorder-nvr-technical-details.html
  // (Datasheet PDF: www.hikvision.com/.../Datasheet-of-DS-7608NI-K1_8P-NVRD.pdf)
  {
    id: 'hikvision-ds-7608ni-k1-8p',
    manufacturer: 'Hikvision',
    model: 'DS-7608NI-K1/8P',
    category: 'nvr-recorder',
    tagline: '8-ch 4K NVR with 8-port PoE, 1 HDD up to 8 TB',
    verified: true,
    datasheetUrl:
      'https://www.sourcesecurity.com/hikvision-ds-7608ni-k1-8p-network-video-recorder-nvr-technical-details.html',
    specs: {
      channels: 8,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 80,
      maxResolution: '8 MP',
      hddBays: 1,
      hddBayMaxTb: 8,
      poeInPorts: 8,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // https://www.hikvision.com/content/dam/hikvision/products/.../Datasheet-of-DS-7608NI-K1-NVRD_V4.71.200_20221031.pdf
  {
    id: 'hikvision-ds-7608ni-k1',
    manufacturer: 'Hikvision',
    model: 'DS-7608NI-K1',
    category: 'nvr-recorder',
    tagline: '8-ch 4K NVR, 1 HDD up to 6 TB, no built-in PoE',
    verified: true,
    datasheetUrl:
      'https://www.hikvision.com/content/dam/hikvision/products/S000000001/S000000002/S000000007/S000000026/OFR000040/M000043526/Data_Sheet/Datasheet-of-DS-7608NI-K1-NVRD_V4.71.200_20221031.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 80,
      maxResolution: '8 MP',
      hddBays: 1,
      hddBayMaxTb: 6,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // https://www.hikvision.com/my/products/IP-Products/Network-Video-Recorders/Pro-Series/ds-7616ni-k1/
  // (Datasheet PDF: www.hikvision.com/.../Datasheet-of-DS-7616NI-K1-NVRD_V4.71.200_20221031.pdf)
  {
    id: 'hikvision-ds-7616ni-k1',
    manufacturer: 'Hikvision',
    model: 'DS-7616NI-K1',
    category: 'nvr-recorder',
    tagline: '16-ch 4K NVR, 1 HDD up to 6 TB, no built-in PoE',
    verified: true,
    datasheetUrl:
      'https://www.hikvision.com/content/dam/hikvision/products/S000000001/S000000002/S000000007/S000000026/OFR000040/M000043528/Data_Sheet/Datasheet-of-DS-7616NI-K1-NVRD_V4.71.200_20221031.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 80,
      maxResolution: '8 MP',
      hddBays: 1,
      hddBayMaxTb: 6,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // https://www.router-switch.com/pdf/ds-7616ni-q1-datasheet.pdf (Q-series)
  {
    id: 'hikvision-ds-7616ni-q1',
    manufacturer: 'Hikvision',
    model: 'DS-7616NI-Q1',
    category: 'nvr-recorder',
    tagline: '16-ch Q-series 4K NVR, 1 HDD, no PoE',
    verified: true,
    datasheetUrl:
      'https://www.hikvision.com/en/products/IP-Products/Network-Video-Recorders/Pro-Series/ds-7616ni-q1/',
    specs: {
      channels: 16,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 80,
      maxResolution: '8 MP',
      hddBays: 1,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Pro Series 7600 / 7600 Q2 (2 HDD)
  // ─────────────────────────────────────────────────────────────────────

  // https://www.hikvision.com/content/dam/hikvision/products/.../Datasheet-of-DS-7608NI-Q1_8P-NVRD_V4.71.200_20221031.pdf
  {
    id: 'hikvision-ds-7608ni-q1-8p',
    manufacturer: 'Hikvision',
    model: 'DS-7608NI-Q1/8P',
    category: 'nvr-recorder',
    tagline: '8-ch Q-series 4K NVR with 8-port PoE, 1 HDD up to 8 TB',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000000570/Datasheet-of-DS-7608NI-Q1_8P-NVRD_V4.71.200_20221031.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 80,
      maxResolution: '8 MP',
      hddBays: 1,
      hddBayMaxTb: 8,
      poeInPorts: 8,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // https://www.hikvision.com/content/dam/hikvision/products/.../Datasheet-of-DS-7616NI-Q2_16P-NVRD_V4.71.200_20221031.pdf
  {
    id: 'hikvision-ds-7616ni-q2-16p',
    manufacturer: 'Hikvision',
    model: 'DS-7616NI-Q2/16P',
    category: 'nvr-recorder',
    tagline: '16-ch Q-series 4K NVR with 16-port PoE, 2 HDD up to 8 TB',
    verified: true,
    datasheetUrl:
      'https://www.hikvision.com/content/dam/hikvision/products/S000000001/S000000002/S000000007/S000000026/OFR000040/M000000580/Data_Sheet/Datasheet-of-DS-7616NI-Q2_16P-NVRD_V4.71.200_20221031.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 80,
      maxResolution: '8 MP',
      hddBays: 2,
      hddBayMaxTb: 8,
      poeInPorts: 16,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Pro Series 7600 K2 (2 HDD)
  // ─────────────────────────────────────────────────────────────────────

  // https://www.sourcesecurity.com/hikvision-ds-7608ni-k2-network-video-recorder-nvr-technical-details.html
  // (Datasheet PDF: www.hikvision.com/.../Datasheet-of-DS-7608NI-K2-NVR-D-or-E_4.71.410_20221026.pdf)
  {
    id: 'hikvision-ds-7608ni-k2',
    manufacturer: 'Hikvision',
    model: 'DS-7608NI-K2',
    category: 'nvr-recorder',
    tagline: '8-ch 4K NVR, 2 HDD up to 10 TB each, no built-in PoE',
    verified: true,
    datasheetUrl:
      'https://www.hikvision.com/content/dam/hikvision/products/S000000001/S000000002/S000000007/S000000026/OFR000040/M000000567/Data_Sheet/Datasheet-of-DS-7608NI-K2-NVR-D-or-E_4.71.410_20221026.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 160,
      maxResolution: '8 MP',
      hddBays: 2,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // https://www.sourcesecurity.com/hikvision-ds-7616ni-k2-network-video-recorder-nvr-technical-details.html
  // (Datasheet PDF: www.hikvision.com/.../Datasheet-of-DS-7616NI-K2-NVR-D-or-E_4.71.410_20221026.pdf)
  {
    id: 'hikvision-ds-7616ni-k2',
    manufacturer: 'Hikvision',
    model: 'DS-7616NI-K2',
    category: 'nvr-recorder',
    tagline: '16-ch 4K NVR, 2 HDD up to 10 TB each, no built-in PoE',
    verified: true,
    datasheetUrl:
      'https://www.hikvision.com/content/dam/hikvision/products/S000000001/S000000002/S000000007/S000000026/OFR000040/M000000576/Data_Sheet/Datasheet-of-DS-7616NI-K2-NVR-D-or-E_4.71.410_20221026.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 160,
      maxResolution: '8 MP',
      hddBays: 2,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // https://www.sourcesecurity.com/hikvision-ds-7616ni-k2-16p-network-video-recorder-nvr-technical-details.html
  // (Datasheet PDF: www.hikvision.com/.../Datasheet-of-DS-7616NI-K2_16P-NVR-D-or-E_4.71.410_20221026.pdf)
  {
    id: 'hikvision-ds-7616ni-k2-16p',
    manufacturer: 'Hikvision',
    model: 'DS-7616NI-K2/16P',
    category: 'nvr-recorder',
    tagline: '16-ch 4K NVR with 16-port PoE, 2 HDD, 160 Mbps',
    verified: true,
    datasheetUrl:
      'https://www.hikvision.com/content/dam/hikvision/products/S000000001/S000000002/S000000007/S000000026/OFR000040/M000000577/Data_Sheet/Datasheet-of-DS-7616NI-K2_16P-NVR-D-or-E_4.71.410_20221026.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 160,
      maxResolution: '8 MP',
      hddBays: 2,
      hddBayMaxTb: 6,
      poeInPorts: 16,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 1 out',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // https://www.hikvision.com/content/dam/hikvision/products/.../Datasheet-of-DS-7632NI-K2-NVR-D-or-E_4.71.410_20221026.pdf
  {
    id: 'hikvision-ds-7632ni-k2',
    manufacturer: 'Hikvision',
    model: 'DS-7632NI-K2',
    category: 'nvr-recorder',
    tagline: '32-ch 4K NVR, 2 HDD up to 10 TB each, no built-in PoE',
    verified: true,
    datasheetUrl:
      'https://www.hikvision.com/content/dam/hikvision/products/S000000001/S000000002/S000000007/S000000026/OFR000040/M000000583/Data_Sheet/Datasheet-of-DS-7632NI-K2-NVR-D-or-E_4.71.410_20221026.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 160,
      maxResolution: '8 MP',
      hddBays: 2,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // https://www.securityinformed.com/hikvision-ds-7632ni-k2-16p-network-video-recorder-nvr-technical-details.html
  // (Datasheet PDF: www.hikvision.com/.../Datasheet-of-DS-7632NI-K2_16P-NVR-D-or-E_4.71.410_20221026.pdf)
  {
    id: 'hikvision-ds-7632ni-k2-16p',
    manufacturer: 'Hikvision',
    model: 'DS-7632NI-K2/16P',
    category: 'nvr-recorder',
    tagline: '32-ch 4K NVR with 16-port PoE, 2 HDD up to 16 TB, 256 Mbps',
    verified: true,
    datasheetUrl:
      'https://www.hikvision.com/content/dam/hikvision/products/S000000001/S000000002/S000000007/S000000026/OFR000040/M000000584/Data_Sheet/Datasheet-of-DS-7632NI-K2_16P-NVR-D-or-E_4.71.410_20221026.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 160,
      maxResolution: '8 MP',
      hddBays: 2,
      hddBayMaxTb: 16,
      poeInPorts: 16,
      poeBudgetW: 200,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Pro Series 7700 K4 (4 HDD, 1.5U)
  // ─────────────────────────────────────────────────────────────────────

  // https://www.sourcesecurity.com/hikvision-ds-7716ni-k4-16p-network-video-recorder-nvr-technical-details.html
  // (Datasheet PDF: download.discomp.cz/hikvision/datasheets/DS-7716NI-K4_16P.pdf)
  {
    id: 'hikvision-ds-7716ni-k4-16p',
    manufacturer: 'Hikvision',
    model: 'DS-7716NI-K4/16P',
    category: 'nvr-recorder',
    tagline: '16-ch 4K NVR with 16-port PoE (200 W), 4 HDD bays, 160 Mbps',
    verified: true,
    datasheetUrl:
      'https://www.sourcesecurity.com/hikvision-ds-7716ni-k4-16p-network-video-recorder-nvr-technical-details.html',
    specs: {
      channels: 16,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 160,
      maxResolution: '8 MP',
      hddBays: 4,
      hddBayMaxTb: 8,
      poeInPorts: 16,
      poeBudgetW: 200,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '2U', // datasheet 1.5U
      alarmIO: '16 in / 4 out',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // https://www.securityinformed.com/hikvision-ds-7732ni-k4-network-video-recorder-nvr-technical-details.html
  // (Datasheet PDF: www.hikvision.com/.../Datasheet-of-DS-7732NI-K4-NVR-D-or-E_V4.71.400_20230920.pdf)
  {
    id: 'hikvision-ds-7732ni-k4',
    manufacturer: 'Hikvision',
    model: 'DS-7732NI-K4',
    category: 'nvr-recorder',
    tagline: '32-ch 4K NVR, 4 HDD up to 10 TB each, 256/160 Mbps, no PoE',
    verified: true,
    datasheetUrl:
      'https://www.hikvision.com/content/dam/hikvision/products/S000000001/S000000132/S000000007/S000000026/OFR031188/M000000602/Data_Sheet/Datasheet-of-DS-7732NI-K4-NVR-D-or-E_V4.71.400_20230920.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 160,
      maxResolution: '12 MP',
      hddBays: 4,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '2U', // datasheet 1.5U
      alarmIO: '16 in / 4 out',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // https://www.securityinformed.com/hikvision-ds-7732ni-k4-16p-network-video-recorder-nvr-technical-details.html
  // (Datasheet PDF: www.getic.com/.../datasheetofds7732nik4_16pnvre_471410_20221026.pdf)
  {
    id: 'hikvision-ds-7732ni-k4-16p',
    manufacturer: 'Hikvision',
    model: 'DS-7732NI-K4/16P',
    category: 'nvr-recorder',
    tagline: '32-ch 4K NVR with 16-port PoE, 4 HDD bays, 256 Mbps',
    verified: true,
    datasheetUrl:
      'https://www.securityinformed.com/hikvision-ds-7732ni-k4-16p-network-video-recorder-nvr-technical-details.html',
    specs: {
      channels: 32,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 160,
      maxResolution: '8 MP',
      hddBays: 4,
      hddBayMaxTb: 10,
      poeInPorts: 16,
      poeBudgetW: 200,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '2U', // datasheet 1.5U
      alarmIO: '16 in / 4 out',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Pro Series 7700 I4 (AcuSense, 4 HDD)
  // ─────────────────────────────────────────────────────────────────────

  // https://www.router-switch.com/pdf2html/pdf/ds-7716ni-i4-datasheet.pdf
  // (Hikvision-hosted: download.discomp.cz/hikvision/datasheets/DS-7716NI-I4_B.pdf)
  {
    id: 'hikvision-ds-7716ni-i4',
    manufacturer: 'Hikvision',
    model: 'DS-7716NI-I4',
    category: 'nvr-recorder',
    tagline: '16-ch I-series NVR, 4 HDD up to 6 TB, 160 Mbps, no PoE',
    verified: true,
    datasheetUrl:
      'https://download.discomp.cz/hikvision/datasheets/DS-7716NI-I4_B.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 256,
      maxResolution: '12 MP',
      hddBays: 4,
      hddBayMaxTb: 6,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '2U', // datasheet 1.5U
      alarmIO: '16 in / 4 out',
      networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },

  // https://objects.eanixter.com/PD553883.PDF
  // (Hikvision DS-7716NI-I4/16P; lssecurity.ca mirror: ds-7716ni-i4_16p.pdf)
  {
    id: 'hikvision-ds-7716ni-i4-16p',
    manufacturer: 'Hikvision',
    model: 'DS-7716NI-I4/16P',
    category: 'nvr-recorder',
    tagline: '16-ch I-series NVR + 16-port PoE (200 W), 4 HDD up to 10 TB',
    verified: true,
    datasheetUrl: 'https://objects.eanixter.com/PD553883.PDF',
    specs: {
      channels: 16,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 256,
      maxResolution: '12 MP',
      hddBays: 4,
      hddBayMaxTb: 10,
      poeInPorts: 16,
      poeBudgetW: 200,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '2U', // datasheet 1.5U
      alarmIO: '16 in / 4 out',
      networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },

  // https://www.sourcesecurity.com/hikvision-ds-7732ni-i4-network-video-recorder-nvr-technical-details.html
  // (Hikvision-hosted PDF: objects.eanixter.com/PD554047.PDF)
  {
    id: 'hikvision-ds-7732ni-i4',
    manufacturer: 'Hikvision',
    model: 'DS-7732NI-I4',
    category: 'nvr-recorder',
    tagline: '32-ch I-series NVR, 4 HDD bays, 320/256 Mbps, no PoE',
    verified: true,
    datasheetUrl:
      'https://www.sourcesecurity.com/hikvision-ds-7732ni-i4-network-video-recorder-nvr-technical-details.html',
    specs: {
      channels: 32,
      inputBandwidthMbps: 320,
      outputBandwidthMbps: 256,
      maxResolution: '12 MP',
      hddBays: 4,
      hddBayMaxTb: 6,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '2U', // datasheet 1.5U
      alarmIO: '16 in / 4 out',
      networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // NXI K1 — AcuSense, 1 HDD, 1U
  // ─────────────────────────────────────────────────────────────────────

  // https://www.hikvision.com/content/dam/hikvision/products/.../Datasheet-of-DS-7604NXI-K1_NVRB_V4.74.000_20230209.pdf
  {
    id: 'hikvision-ds-7604nxi-k1',
    manufacturer: 'Hikvision',
    model: 'DS-7604NXI-K1',
    category: 'nvr-recorder',
    tagline: '4-ch AcuSense 4K NVR, 1 HDD up to 10 TB, no built-in PoE',
    verified: true,
    datasheetUrl:
      'https://www.hikvision.com/content/dam/hikvision/products/S000000001/S000000002/S000000007/S000000026/OFR000042/M000058881/Data_Sheet/Datasheet-of-DS-7604NXI-K1_NVRB_V4.74.000_20230209.pdf',
    specs: {
      channels: 4,
      inputBandwidthMbps: 40,
      outputBandwidthMbps: 80,
      maxResolution: '12 MP',
      hddBays: 1,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // https://download.axilogi.com/Hikvision/Datasheet/DS-7604NXI-K1_4P.pdf
  {
    id: 'hikvision-ds-7604nxi-k1-4p',
    manufacturer: 'Hikvision',
    model: 'DS-7604NXI-K1/4P',
    category: 'nvr-recorder',
    tagline: '4-ch AcuSense 4K NVR with 4-port PoE (50 W), 1 HDD up to 10 TB',
    verified: true,
    datasheetUrl:
      'https://download.axilogi.com/Hikvision/Datasheet/DS-7604NXI-K1_4P.pdf',
    specs: {
      channels: 4,
      inputBandwidthMbps: 40,
      outputBandwidthMbps: 80,
      maxResolution: '12 MP',
      hddBays: 1,
      hddBayMaxTb: 10,
      poeInPorts: 4,
      poeBudgetW: 50,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // https://download.discomp.cz/hikvision/datasheets/DS-7608NXI-K1.pdf
  {
    id: 'hikvision-ds-7608nxi-k1',
    manufacturer: 'Hikvision',
    model: 'DS-7608NXI-K1',
    category: 'nvr-recorder',
    tagline: '8-ch AcuSense 4K NVR, 1 HDD up to 10 TB, no built-in PoE',
    verified: true,
    datasheetUrl:
      'https://download.discomp.cz/hikvision/datasheets/DS-7608NXI-K1.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 80,
      maxResolution: '12 MP',
      hddBays: 1,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // https://www.hikvision.com/content/dam/hikvision/products/.../Datasheet-of-DS-7608NXI-K1_8P_NVRB_V4.74.000_20230209.pdf
  {
    id: 'hikvision-ds-7608nxi-k1-8p',
    manufacturer: 'Hikvision',
    model: 'DS-7608NXI-K1/8P',
    category: 'nvr-recorder',
    tagline: '8-ch AcuSense 4K NVR with 8-port PoE, 1 HDD up to 10 TB',
    verified: true,
    datasheetUrl:
      'https://www.hikvision.com/content/dam/hikvision/products/S000000001/S000000002/S000000007/S000000026/OFR000042/M000058878/Data_Sheet/Datasheet-of-DS-7608NXI-K1_8P_NVRB_V4.74.000_20230209.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 80,
      maxResolution: '12 MP',
      hddBays: 1,
      hddBayMaxTb: 10,
      poeInPorts: 8,
      poeBudgetW: 80,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // https://download.discomp.cz/hikvision/datasheets/DS-7616NXI-K1.pdf
  {
    id: 'hikvision-ds-7616nxi-k1',
    manufacturer: 'Hikvision',
    model: 'DS-7616NXI-K1',
    category: 'nvr-recorder',
    tagline: '16-ch AcuSense 4K NVR, 1 HDD up to 10 TB, no built-in PoE',
    verified: true,
    datasheetUrl:
      'https://download.discomp.cz/hikvision/datasheets/DS-7616NXI-K1.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 80,
      maxResolution: '12 MP',
      hddBays: 1,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // NXI K2 — AcuSense, 2 HDD
  // ─────────────────────────────────────────────────────────────────────

  // https://www.hikvision.com/content/dam/hikvision/products/.../Datasheet-of-DS-7616NXI-K2_16P_V4.74.000_20230209.pdf
  {
    id: 'hikvision-ds-7616nxi-k2-16p',
    manufacturer: 'Hikvision',
    model: 'DS-7616NXI-K2/16P',
    category: 'nvr-recorder',
    tagline: '16-ch AcuSense 4K NVR with 16-port PoE, 2 HDD up to 10 TB each',
    verified: true,
    datasheetUrl:
      'https://www.hikvision.com/content/dam/hikvision/products/S000000001/S000000132/S000000007/S000000026/OFR031212/M000058884/Data_Sheet/Datasheet-of-DS-7616NXI-K2_16P_V4.74.000_20230209.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 160,
      maxResolution: '12 MP',
      hddBays: 2,
      hddBayMaxTb: 10,
      poeInPorts: 16,
      poeBudgetW: 200,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // https://www.hikvision.com/europe/products/IP-Products/Network-Video-Recorders/Pro-Series/ds-7632nxi-k2/
  // (Datasheet: geovision.bg/.../Datasheet-of-DS-7632NXI-K2_V4.74.000_20230209.pdf)
  {
    id: 'hikvision-ds-7632nxi-k2',
    manufacturer: 'Hikvision',
    model: 'DS-7632NXI-K2',
    category: 'nvr-recorder',
    tagline: '32-ch AcuSense 4K NVR, 2 HDD up to 10 TB each, no built-in PoE',
    verified: true,
    datasheetUrl:
      'https://www.geovision.bg/uploads/docs/716_bg_Datasheet-of-DS-7632NXI-K2_V4.74.000_20230209.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 160,
      maxResolution: '12 MP',
      hddBays: 2,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // https://gfx3.senetic.com/.../Datasheet_of_DS_7632NXI_K2_16P_V4.74.000_20230209.pdf
  {
    id: 'hikvision-ds-7632nxi-k2-16p',
    manufacturer: 'Hikvision',
    model: 'DS-7632NXI-K2/16P',
    category: 'nvr-recorder',
    tagline: '32-ch AcuSense 4K NVR with 16-port PoE, 2 HDD up to 10 TB each',
    verified: true,
    datasheetUrl:
      'https://gfx3.senetic.com/akeneo-catalog/b/9/6/9/b9697992027af9c6384191e3417af502304091fa_Datasheet_of_DS_7632NXI_K2_16P_V4.74.000_20230209.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 160,
      maxResolution: '12 MP',
      hddBays: 2,
      hddBayMaxTb: 10,
      poeInPorts: 16,
      poeBudgetW: 200,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // NXI K4 — AcuSense, 4 HDD, 1.5U
  // ─────────────────────────────────────────────────────────────────────

  // https://assets.hikvision.com/prd/public/all/doc/m000059582/Datasheet-of-DS-7716NXI-K4_V4.74.000_20230104.pdf
  {
    id: 'hikvision-ds-7716nxi-k4',
    manufacturer: 'Hikvision',
    model: 'DS-7716NXI-K4',
    category: 'nvr-recorder',
    tagline: '16-ch AcuSense 4K NVR, 4 HDD bays, 160 Mbps, no built-in PoE',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000059582/Datasheet-of-DS-7716NXI-K4_V4.74.000_20230104.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 160,
      maxResolution: '12 MP',
      hddBays: 4,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '2U', // datasheet 1.5U
      alarmIO: '16 in / 4 out (16/9 optional)',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // https://www.hikvision.com/content/dam/hikvision/mena/support/sira/admcc-2023-oliver/DatasheetofDS-7732NXI-K4_16P_V4.74.000_20230104.pdf
  {
    id: 'hikvision-ds-7732nxi-k4-16p',
    manufacturer: 'Hikvision',
    model: 'DS-7732NXI-K4/16P',
    category: 'nvr-recorder',
    tagline: '32-ch AcuSense 4K NVR with 16-port PoE (200 W), 4 HDD up to 10 TB',
    verified: true,
    datasheetUrl:
      'https://www.hikvision.com/content/dam/hikvision/mena/support/sira/admcc-2023-oliver/DatasheetofDS-7732NXI-K4_16P_V4.74.000_20230104.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 160,
      maxResolution: '12 MP',
      hddBays: 4,
      hddBayMaxTb: 10,
      poeInPorts: 16,
      poeBudgetW: 200,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '2U', // datasheet 1.5U
      alarmIO: '16 in / 4 out (16/9 optional)',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // NXI I4/S — AcuSense, 4 HDD, with /S Smart variants
  // ─────────────────────────────────────────────────────────────────────

  // https://assets.hikvision.com/prd/public/all/doc/sm000060191/DS-7716NXI-I4_SE_Datasheet_20250114.pdf
  {
    id: 'hikvision-ds-7716nxi-i4-s',
    manufacturer: 'Hikvision',
    model: 'DS-7716NXI-I4/S',
    category: 'nvr-recorder',
    tagline: '16-ch AcuSense 4K NVR (I-series), 4 HDD, 256 Mbps, no built-in PoE',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/sm000060191/DS-7716NXI-I4_SE_Datasheet_20250114.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
      maxResolution: '12 MP',
      hddBays: 4,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '2U', // datasheet 1.5U
      alarmIO: '16 in / 4 out',
      networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },

  // https://www.bhphotovideo.com/lit_files/573207.pdf
  // (DS-7716NXI-I4/16P/S spec sheet via B&H from Hikvision)
  {
    id: 'hikvision-ds-7716nxi-i4-16p-s',
    manufacturer: 'Hikvision',
    model: 'DS-7716NXI-I4/16P/S',
    category: 'nvr-recorder',
    tagline: '16-ch AcuSense 4K NVR with 16-port PoE, 4 HDD bays, deep-learning analytics',
    verified: true,
    datasheetUrl: 'https://www.bhphotovideo.com/lit_files/573207.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
      maxResolution: '12 MP',
      hddBays: 4,
      hddBayMaxTb: 10,
      poeInPorts: 16,
      poeBudgetW: 200,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '2U', // datasheet 1.5U
      alarmIO: '16 in / 4 out',
      networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },

  // https://www.bhphotovideo.com/lit_files/573207.pdf
  // (DS-7732NXI-I4/16P/S 32-ch variant — same spec doc family)
  {
    id: 'hikvision-ds-7732nxi-i4-16p-s',
    manufacturer: 'Hikvision',
    model: 'DS-7732NXI-I4/16P/S',
    category: 'nvr-recorder',
    tagline: '32-ch AcuSense 4K NVR with 16-port PoE, 4 HDD bays, 320 Mbps',
    verified: true,
    datasheetUrl: 'https://www.bhphotovideo.com/lit_files/573207.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 320,
      outputBandwidthMbps: 256,
      maxResolution: '12 MP',
      hddBays: 4,
      hddBayMaxTb: 10,
      poeInPorts: 16,
      poeBudgetW: 200,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '2U', // datasheet 1.5U
      alarmIO: '16 in / 4 out',
      networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Ultra Series 9600 I8 / I16 (Enterprise, RAID, dual GbE)
  // ─────────────────────────────────────────────────────────────────────

  // https://www.securityinformed.com/hikvision-ds-9616ni-i8-network-video-recorder-nvr-technical-details.html
  // (Hikvision PDF mirror: surveillance-video.com/.../DS-9616NI-I8.pdf)
  {
    id: 'hikvision-ds-9616ni-i8',
    manufacturer: 'Hikvision',
    model: 'DS-9616NI-I8',
    category: 'nvr-recorder',
    tagline: '16-ch Ultra NVR, 8 HDD bays + RAID 0/1/5/6/10, 256 Mbps',
    verified: true,
    datasheetUrl:
      'https://www.surveillance-video.com/media/lanot/attachments/customimport/DS-9616NI-I8.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
      maxResolution: '12 MP',
      hddBays: 8,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '2U',
      networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },

  // https://www.sourcesecurity.com/hikvision-ds-9632ni-i8-network-video-recorder-nvr-technical-details.html
  // (Hikvision PDF: www.router-switch.com/pdf2html/pdf/ds-9632ni-i8-datasheet.pdf)
  {
    id: 'hikvision-ds-9632ni-i8',
    manufacturer: 'Hikvision',
    model: 'DS-9632NI-I8',
    category: 'nvr-recorder',
    tagline: '32-ch Ultra NVR, 8 HDD bays up to 10 TB, RAID, 320 Mbps',
    verified: true,
    datasheetUrl:
      'https://www.sourcesecurity.com/hikvision-ds-9632ni-i8-network-video-recorder-nvr-technical-details.html',
    specs: {
      channels: 32,
      inputBandwidthMbps: 320,
      outputBandwidthMbps: 256,
      maxResolution: '12 MP',
      hddBays: 8,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '2U',
      networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },

  // https://www.hikvision.com/en/products/IP-Products/Network-Video-Recorders/Ultra-Series/DS-9616NI-I16/
  // (Datasheet mirror: files.eetgroup.com/Hikvision/Datasheets/DS-9664NI-I16.pdf — same family)
  {
    id: 'hikvision-ds-9616ni-i16',
    manufacturer: 'Hikvision',
    model: 'DS-9616NI-I16',
    category: 'nvr-recorder',
    tagline: '16-ch enterprise NVR, 16 HDD bays + RAID 0/1/5/6/10, hot-spare',
    verified: true,
    datasheetUrl:
      'https://www.hikvision.com/en/products/IP-Products/Network-Video-Recorders/Ultra-Series/DS-9616NI-I16/',
    specs: {
      channels: 16,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
      maxResolution: '12 MP',
      hddBays: 16,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '3U',
      networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },

  // https://www.router-switch.com/pdf2html/pdf/ds-9632ni-i16-datasheet.pdf
  {
    id: 'hikvision-ds-9632ni-i16',
    manufacturer: 'Hikvision',
    model: 'DS-9632NI-I16',
    category: 'nvr-recorder',
    tagline: '32-ch enterprise NVR, 16 HDD + 1 eSATA, RAID 0/1/5/6/10, 320 Mbps',
    verified: true,
    datasheetUrl:
      'https://www.securitysourcing.net/src/pdf/DS-9632NI-I16.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 320,
      outputBandwidthMbps: 256,
      maxResolution: '12 MP',
      hddBays: 16,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '3U',
      networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },

  // https://www.sourcesecurity.com/hikvision-ds-9664ni-i16-network-video-recorder-nvr-technical-details.html
  // (Datasheet: files.eetgroup.com/Hikvision/Datasheets/DS-9664NI-I16.pdf)
  {
    id: 'hikvision-ds-9664ni-i16',
    manufacturer: 'Hikvision',
    model: 'DS-9664NI-I16',
    category: 'nvr-recorder',
    tagline: '64-ch enterprise NVR, 16 HDD bays, RAID 0/1/5/6/10 + N+1 hot-spare',
    verified: true,
    datasheetUrl:
      'https://files.eetgroup.com/Hikvision/Datasheets/DS-9664NI-I16.pdf',
    specs: {
      channels: 64,
      inputBandwidthMbps: 320,
      outputBandwidthMbps: 256,
      maxResolution: '12 MP',
      hddBays: 16,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '3U',
      networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Ultra Series M-Series 8K
  // ─────────────────────────────────────────────────────────────────────

  // https://www.sourcesecurity.com/hikvision-ds-7732ni-m4-network-video-recorder-nvr-technical-details.html
  // (Hikvision-hosted: assets.hikvision.com/.../Datasheet-of-DS-7732NI-M4_V4.63.010_20230705.pdf)
  {
    id: 'hikvision-ds-7732ni-m4',
    manufacturer: 'Hikvision',
    model: 'DS-7732NI-M4',
    category: 'nvr-recorder',
    tagline: '32-ch M-series 8K-capable NVR, 4 HDD up to 14 TB, 320 Mbps',
    verified: true,
    datasheetUrl:
      'https://www.sourcesecurity.com/hikvision-ds-7732ni-m4-network-video-recorder-nvr-technical-details.html',
    specs: {
      channels: 32,
      inputBandwidthMbps: 320,
      maxResolution: '32 MP',
      hddBays: 4,
      hddBayMaxTb: 14,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '2U', // datasheet 1.5U
      alarmIO: '16 in / 9 out',
      networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },

  // https://www.hikvision.com/content/dam/hikvision/products/.../Datasheet-of-DS-9664NI-M8_V4.63.010_20230705.pdf
  {
    id: 'hikvision-ds-9664ni-m8',
    manufacturer: 'Hikvision',
    model: 'DS-9664NI-M8',
    category: 'nvr-recorder',
    tagline: '64-ch M-series 8K NVR, 8 HDD up to 14 TB (112 TB total), RAID',
    verified: true,
    datasheetUrl:
      'https://www.hikvision.com/content/dam/hikvision/products/S000000001/S000000002/S000000007/S000000008/OFR000006/M000000746/Data_Sheet/Datasheet-of-DS-9664NI-M8_V4.63.010_20230705.pdf',
    specs: {
      channels: 64,
      inputBandwidthMbps: 400,
      outputBandwidthMbps: 400,
      maxResolution: '32 MP',
      hddBays: 8,
      hddBayMaxTb: 14,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '2U',
      networkPorts: '2× 10/100/1000 Mbps RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Solar / Compact eSSD NVRs
  // ─────────────────────────────────────────────────────────────────────

  // https://www.hikvision.com/content/dam/hikvision/products/.../Datasheet-of-DS-E04NI-Q1_4P_SSD-NVR_V4.30.067_20240412.pdf
  {
    id: 'hikvision-ds-e04ni-q1-4p',
    manufacturer: 'Hikvision',
    model: 'DS-E04NI-Q1/4P',
    category: 'nvr-recorder',
    tagline: '4-ch ultra-compact NVR, built-in 2 TB eSSD, 4-port PoE, <10 W',
    verified: true,
    datasheetUrl:
      'https://www.hikvision.com/content/dam/hikvision/products/S000000001/S000000132/S000000007/S000000018/OFR001755/M000084601/Data_Sheet/Datasheet-of-DS-E04NI-Q1_4P_SSD-NVR_V4.30.067_20240412.pdf',
    specs: {
      channels: 4,
      inputBandwidthMbps: 40,
      outputBandwidthMbps: 60,
      maxResolution: '4 MP',
      hddBays: 0, // built-in eSSD, no swappable bay
      poeInPorts: 4,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× 10/100 Mbps RJ-45',
    },
  },

  // https://assets.hikvision.com/prd/public/all/doc/m000084600/Datasheet-of-DS-E04NI-Q1_SSD-1T-NVR_V4.30.067_20230817.pdf
  {
    id: 'hikvision-ds-e04ni-q1',
    manufacturer: 'Hikvision',
    model: 'DS-E04NI-Q1',
    category: 'nvr-recorder',
    tagline: '4-ch ultra-compact NVR, built-in 1 TB eSSD, no PoE, <10 W',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000084600/Datasheet-of-DS-E04NI-Q1_SSD-1T-NVR_V4.30.067_20230817.pdf',
    specs: {
      channels: 4,
      inputBandwidthMbps: 40,
      outputBandwidthMbps: 60,
      maxResolution: '4 MP',
      hddBays: 0, // built-in eSSD
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× 10/100 Mbps RJ-45',
    },
  },

  // https://assets.hikvision.com/prd/public/all/doc/m000084603/Datasheet-of-DS-E08NI-Q1_8P_SSD-NVR_V4.30.067_20240412.pdf
  {
    id: 'hikvision-ds-e08ni-q1-8p',
    manufacturer: 'Hikvision',
    model: 'DS-E08NI-Q1/8P',
    category: 'nvr-recorder',
    tagline: '8-ch ultra-compact NVR, built-in 2 TB eSSD, 8-port PoE, <10 W',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000084603/Datasheet-of-DS-E08NI-Q1_8P_SSD-NVR_V4.30.067_20240412.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 60,
      outputBandwidthMbps: 60,
      maxResolution: '4 MP',
      hddBays: 0, // built-in eSSD
      poeInPorts: 8,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× 10/100 Mbps RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Turbo HD DVR — AcuSense (iDS-7200 / iDS-7300)
  // Hybrid HD-TVI/AHD/CVI/CVBS/IP (hybrid: true)
  // ─────────────────────────────────────────────────────────────────────

  // https://assets.hikvision.com/prd/public/all/doc/m000007976/Datasheet-of-iDS-7204HQHI-M1_S_V4.71.000_20240110.pdf
  {
    id: 'hikvision-ids-7204hqhi-m1-s',
    manufacturer: 'Hikvision',
    model: 'iDS-7204HQHI-M1/S',
    category: 'nvr-recorder',
    tagline: '4-ch AcuSense hybrid DVR (HD-TVI/AHD/CVI/CVBS/IP), 1 HDD up to 10 TB',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000007976/Datasheet-of-iDS-7204HQHI-M1_S_V4.71.000_20240110.pdf',
    specs: {
      channels: 4,
      maxResolution: '5 MP', // 3K/5MP Lite @ 12 fps; analog HD-TVI 4 MP
      hddBays: 1,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100 Mbps RJ-45',
      hybrid: true,
    },
  },

  // https://www.router-switch.com/pdf2html/pdf/ids-7208hqhi-m1-s-datasheet.pdf
  {
    id: 'hikvision-ids-7208hqhi-m1-s',
    manufacturer: 'Hikvision',
    model: 'iDS-7208HQHI-M1/S',
    category: 'nvr-recorder',
    tagline: '8-ch AcuSense hybrid DVR (HD-TVI/AHD/CVI/CVBS/IP), 1 HDD up to 10 TB',
    verified: true,
    datasheetUrl:
      'https://www.router-switch.com/pdf2html/pdf/ids-7208hqhi-m1-s-datasheet.pdf',
    specs: {
      channels: 8,
      maxResolution: '5 MP', // 3K/5MP Lite; analog HD-TVI 4 MP
      hddBays: 1,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100 Mbps RJ-45',
      hybrid: true,
    },
  },

  // https://assets.hikvision.com/prd/public/all/doc/m000058564/Datasheet-of-iDS-7208HUHI-M1_E_V4.71.000_20230621.pdf
  {
    id: 'hikvision-ids-7208huhi-m1-e',
    manufacturer: 'Hikvision',
    model: 'iDS-7208HUHI-M1/E',
    category: 'nvr-recorder',
    tagline: '8-ch AcuSense hybrid DVR (HD-TVI 8 MP), 1 HDD, deep-learning analytics',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000058564/Datasheet-of-iDS-7208HUHI-M1_E_V4.71.000_20230621.pdf',
    specs: {
      channels: 8,
      maxResolution: '8 MP', // HD-TVI up to 8 MP @ 8 fps
      hddBays: 1,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100 Mbps RJ-45',
      hybrid: true,
    },
  },

  // https://www.hikvision.com/content/dam/hikvision/products/.../Datasheet-of-iDS-7208HUHI-M1_X_V4.71.500_20240311.pdf
  {
    id: 'hikvision-ids-7208huhi-m1-s',
    manufacturer: 'Hikvision',
    model: 'iDS-7208HUHI-M1/S',
    category: 'nvr-recorder',
    tagline: '8-ch AcuSense hybrid DVR (HD-TVI 8 MP), 1 HDD, motion-detect 2.0',
    verified: true,
    datasheetUrl:
      'https://www.hikvision.com/content/dam/hikvision/products/S000000001/S000000132/S000000133/S000000821/OFR000174/M000120174/Data_Sheet/Datasheet-of-iDS-7208HUHI-M1_X_V4.71.500_20240311.pdf',
    specs: {
      channels: 8,
      maxResolution: '8 MP',
      hddBays: 1,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100 Mbps RJ-45',
      hybrid: true,
    },
  },

  // https://assets.hikvision.com/prd/public/all/doc/m000120175/iDS-7208HUHI-M2_X_Datasheet_20241018.pdf
  {
    id: 'hikvision-ids-7208huhi-m2-s',
    manufacturer: 'Hikvision',
    model: 'iDS-7208HUHI-M2/S',
    category: 'nvr-recorder',
    tagline: '8-ch AcuSense hybrid DVR, 2 HDD bays, HD-TVI up to 8 MP',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000120175/iDS-7208HUHI-M2_X_Datasheet_20241018.pdf',
    specs: {
      channels: 8,
      maxResolution: '8 MP',
      hddBays: 2,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100 Mbps RJ-45',
      hybrid: true,
    },
  },

  // https://www.hikvision.com/content/dam/hikvision/products/.../Datasheet-of-iDS-7216HUHI-M2_S_V4.71.000_20230621.pdf
  {
    id: 'hikvision-ids-7216huhi-m2-s',
    manufacturer: 'Hikvision',
    model: 'iDS-7216HUHI-M2/S',
    category: 'nvr-recorder',
    tagline: '16-ch AcuSense hybrid DVR, 2 HDD up to 20 TB, HD-TVI 8 MP',
    verified: true,
    datasheetUrl:
      'https://www.hikvision.com/content/dam/hikvision/products/S000000001/S000000132/S000000133/S000000821/OFR000174/M000033682/Data_Sheet/Datasheet-of-iDS-7216HUHI-M2_S_V4.71.000_20230621.pdf',
    specs: {
      channels: 16,
      maxResolution: '8 MP',
      hddBays: 2,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
      hybrid: true,
    },
  },

  // https://www.a1securitycameras.com/content/product_documents/63642/Datasheet_plkmdapg.pdf
  // (iDS-7332HUHI-M4/S; closest documented sibling of 7316HUHI-M4/S)
  // 7316/M4/S verified from:
  // https://www.hikvision.com/us-en/products/turbo-hd-products/dvr/pro-series/ids-7316huhi-m4-s/
  {
    id: 'hikvision-ids-7316huhi-m4-s',
    manufacturer: 'Hikvision',
    model: 'iDS-7316HUHI-M4/S',
    category: 'nvr-recorder',
    tagline: '16-ch AcuSense hybrid DVR, 4 HDD up to 12 TB, RAID 0/1/5/6/10',
    verified: true,
    datasheetUrl:
      'https://www.hikvision.com/us-en/products/turbo-hd-products/dvr/pro-series/ids-7316huhi-m4-s/',
    specs: {
      channels: 16,
      maxResolution: '8 MP',
      hddBays: 4,
      hddBayMaxTb: 12,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '2U', // datasheet 1.5U
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
      hybrid: true,
    },
  },

  // https://assets.hikvision.com/prd/public/all/doc/m000065334/Datasheet-of-iDS-7332HUHI-M4_S_V4.5.0.200_20220901.pdf
  {
    id: 'hikvision-ids-7332huhi-m4-s',
    manufacturer: 'Hikvision',
    model: 'iDS-7332HUHI-M4/S',
    category: 'nvr-recorder',
    tagline: '32-ch AcuSense hybrid DVR, 4 HDD up to 10 TB, HD-TVI 8 MP, RAID',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000065334/Datasheet-of-iDS-7332HUHI-M4_S_V4.5.0.200_20220901.pdf',
    specs: {
      channels: 32,
      maxResolution: '8 MP',
      hddBays: 4,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '2U', // datasheet 1.5U
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
      hybrid: true,
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Turbo HD DVR — Standard (non-AcuSense, DS-7200/7300)
  // Hybrid HD-TVI/AHD/CVI/CVBS/IP (hybrid: true)
  // ─────────────────────────────────────────────────────────────────────

  // https://www.router-switch.com/pdf2html/pdf/ds-7204hqhi-k1-datasheet.pdf
  {
    id: 'hikvision-ds-7204hqhi-k1',
    manufacturer: 'Hikvision',
    model: 'DS-7204HQHI-K1',
    category: 'nvr-recorder',
    tagline: '4-ch Turbo HD hybrid DVR (HD-TVI 4 MP/AHD/CVI/CVBS/IP), 1 HDD',
    verified: true,
    datasheetUrl:
      'https://www.router-switch.com/pdf2html/pdf/ds-7204hqhi-k1-datasheet.pdf',
    specs: {
      channels: 4,
      maxResolution: '4 MP',
      hddBays: 1,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U', // Mini 1U
      networkPorts: '1× 10/100 Mbps RJ-45',
      hybrid: true,
    },
  },

  // https://www.router-switch.com/pdf2html/pdf/ds-7208hqhi-k1-datasheet.pdf
  {
    id: 'hikvision-ds-7208hqhi-k1',
    manufacturer: 'Hikvision',
    model: 'DS-7208HQHI-K1',
    category: 'nvr-recorder',
    tagline: '8-ch Turbo HD hybrid DVR (HD-TVI 4 MP/AHD/CVI/CVBS/IP), 1 HDD',
    verified: true,
    datasheetUrl:
      'https://www.router-switch.com/pdf2html/pdf/ds-7208hqhi-k1-datasheet.pdf',
    specs: {
      channels: 8,
      maxResolution: '6 MP', // IP up to 6 MP; HD-TVI up to 4 MP Lite
      hddBays: 1,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100 Mbps RJ-45',
      hybrid: true,
    },
  },

  // https://www.router-switch.com/pdf2html/pdf/ds-7204huhi-k1-datasheet.pdf
  {
    id: 'hikvision-ds-7204huhi-k1',
    manufacturer: 'Hikvision',
    model: 'DS-7204HUHI-K1',
    category: 'nvr-recorder',
    tagline: '4-ch Turbo HD hybrid DVR (HD-TVI 5 MP/AHD/CVI/CVBS/IP), 1 HDD',
    verified: true,
    datasheetUrl:
      'https://www.router-switch.com/pdf2html/pdf/ds-7204huhi-k1-datasheet.pdf',
    specs: {
      channels: 4,
      maxResolution: '8 MP', // 8 MP Lite @ 15 fps; 5 MP @ 12 fps
      hddBays: 1,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 1 out',
      networkPorts: '1× 10/100 Mbps RJ-45',
      hybrid: true,
    },
  },

  // https://www.sourcesecurity.com/hikvision-ds-7208huhi-k1-digital-video-technical-details.html
  {
    id: 'hikvision-ds-7208huhi-k1',
    manufacturer: 'Hikvision',
    model: 'DS-7208HUHI-K1',
    category: 'nvr-recorder',
    tagline: '8-ch Turbo HD hybrid DVR (HD-TVI 5 MP/AHD/CVI/CVBS/IP), 1 HDD',
    verified: true,
    datasheetUrl:
      'https://www.sourcesecurity.com/hikvision-ds-7208huhi-k1-digital-video-technical-details.html',
    specs: {
      channels: 8,
      maxResolution: '5 MP',
      hddBays: 1,
      hddBayMaxTb: 8,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
      hybrid: true,
    },
  },

  // https://www.sourcesecurity.com/hikvision-ds-7208huhi-k2-p-digital-video-recorder-dvr-technical-details.html
  // (covers DS-7208HUHI-K2 / K2/P shared spec sheet)
  {
    id: 'hikvision-ds-7208huhi-k2',
    manufacturer: 'Hikvision',
    model: 'DS-7208HUHI-K2',
    category: 'nvr-recorder',
    tagline: '8-ch Turbo HD hybrid DVR, 2 HDD up to 8 TB, HD-TVI 8 MP',
    verified: true,
    datasheetUrl:
      'https://www.router-switch.com/pdf2html/pdf/ds-7208huhi-k2-p-datasheet.pdf',
    specs: {
      channels: 8,
      maxResolution: '8 MP', // 8 MP @ 8 fps
      hddBays: 2,
      hddBayMaxTb: 8,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
      hybrid: true,
    },
  },

  // https://www.router-switch.com/pdf2html/pdf/ds-7216huhi-k2-datasheet.pdf
  {
    id: 'hikvision-ds-7216huhi-k2',
    manufacturer: 'Hikvision',
    model: 'DS-7216HUHI-K2',
    category: 'nvr-recorder',
    tagline: '16-ch Turbo HD hybrid DVR, 2 HDD up to 10 TB, HD-TVI 8 MP',
    verified: true,
    datasheetUrl:
      'https://www.router-switch.com/pdf2html/pdf/ds-7216huhi-k2-datasheet.pdf',
    specs: {
      channels: 16,
      maxResolution: '8 MP',
      hddBays: 2,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 1 out',
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
      hybrid: true,
    },
  },

  // https://www.hikvision.com/content/dam/hikvision/products/.../Datasheet-of-DS-7300HUHI-K4_V4.2.0_20220301.pdf
  {
    id: 'hikvision-ds-7308huhi-k4',
    manufacturer: 'Hikvision',
    model: 'DS-7308HUHI-K4',
    category: 'nvr-recorder',
    tagline: '8-ch Turbo HD hybrid DVR, 4 HDD + 1 eSATA, HD-TVI 8 MP',
    verified: true,
    datasheetUrl:
      'https://www.hikvision.com/content/dam/hikvision/products/S000000001/S000000132/S000000133/S000000134/OFR000180/M000005751/Data_Sheet/Datasheet-of-DS-7300HUHI-K4_V4.2.0_20220301.pdf',
    specs: {
      channels: 8,
      maxResolution: '8 MP',
      hddBays: 4,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '2U', // datasheet 1.5U
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
      hybrid: true,
    },
  },

  // https://www.router-switch.com/pdf2html/pdf/ds-7316huhi-k4-datasheet.pdf
  {
    id: 'hikvision-ds-7316huhi-k4',
    manufacturer: 'Hikvision',
    model: 'DS-7316HUHI-K4',
    category: 'nvr-recorder',
    tagline: '16-ch Turbo HD hybrid DVR, 4 HDD + 1 eSATA, HD-TVI 8 MP',
    verified: true,
    datasheetUrl:
      'https://www.router-switch.com/pdf2html/pdf/ds-7316huhi-k4-datasheet.pdf',
    specs: {
      channels: 16,
      maxResolution: '8 MP',
      hddBays: 4,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '2U', // datasheet 1.5U
      networkPorts: '1× 10/100/1000 Mbps RJ-45',
      hybrid: true,
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Turbo HD DVR — Entry / Value (DS-7100HGHI-K1)
  // ─────────────────────────────────────────────────────────────────────

  // https://assets.hikvision.com/prd/public/all/doc/m000018278/Datasheet-of_DS-7104HGHI-K1_V4.70.160_20230822.pdf
  {
    id: 'hikvision-ds-7104hghi-k1',
    manufacturer: 'Hikvision',
    model: 'DS-7104HGHI-K1',
    category: 'nvr-recorder',
    tagline: '4-ch entry hybrid DVR (HD-TVI 1080p Lite), 1 HDD up to 4 TB',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000018278/Datasheet-of_DS-7104HGHI-K1_V4.70.160_20230822.pdf',
    specs: {
      channels: 4,
      maxResolution: '2 MP', // 1080p Lite for analog, IP up to 2 MP
      hddBays: 1,
      hddBayMaxTb: 4,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U', // Mini 1U
      networkPorts: '1× 10/100 Mbps RJ-45',
      hybrid: true,
    },
  },

  // https://assets.hikvision.com/prd/public/all/doc/m000018279/Datasheet-of_DS-7108HGHI-K1_V4.70.160_20230822.pdf
  {
    id: 'hikvision-ds-7108hghi-k1',
    manufacturer: 'Hikvision',
    model: 'DS-7108HGHI-K1',
    category: 'nvr-recorder',
    tagline: '8-ch entry hybrid DVR (HD-TVI 1080p Lite), 1 HDD up to 4 TB',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000018279/Datasheet-of_DS-7108HGHI-K1_V4.70.160_20230822.pdf',
    specs: {
      channels: 8,
      maxResolution: '5 MP', // IP cam input up to 5 MP
      hddBays: 1,
      hddBayMaxTb: 4,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U', // Mini 1U
      networkPorts: '1× 10/100 Mbps RJ-45',
      hybrid: true,
    },
  },

  // https://assets.hikvision.com/prd/public/all/doc/m000018277/Datasheet-of_DS-7208HGHI-K1_V4.70.160_20230822.pdf
  {
    id: 'hikvision-ds-7208hghi-k1',
    manufacturer: 'Hikvision',
    model: 'DS-7208HGHI-K1',
    category: 'nvr-recorder',
    tagline: '8-ch entry hybrid DVR (HD-TVI 1080p Lite), 1 HDD',
    verified: true,
    datasheetUrl:
      'https://assets.hikvision.com/prd/public/all/doc/m000018277/Datasheet-of_DS-7208HGHI-K1_V4.70.160_20230822.pdf',
    specs: {
      channels: 8,
      maxResolution: '2 MP',
      hddBays: 1,
      hddBayMaxTb: 6,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'], // datasheet also lists H.264+; not in our codec union
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100 Mbps RJ-45',
      hybrid: true,
    },
  },
];
